import { useEffect, useState, useCallback } from 'react';

/**
 * useCustomerView — admin-only UX toggle that masks ALL admin chrome and
 * makes the app look exactly like a customer's view. Persists in sessionStorage
 * so a page reload keeps the mode (useful when an admin is screen-recording
 * a customer flow). Cleared on tab close.
 *
 * Multiple tabs sync via the storage event.
 *
 * Components should treat `isCustomerView === true` as if the user is NOT an
 * admin — i.e. `isAdmin && !isCustomerView` for every admin-only UI gate.
 */
const KEY = 'customer_view_enabled';

const readFlag = () => {
  try { return sessionStorage.getItem(KEY) === '1'; } catch { return false; }
};

export const useCustomerView = () => {
  const [isCustomerView, setState] = useState(readFlag);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setState(readFlag());
    };
    // Custom event used by the toggle to broadcast within the same tab
    const onLocal = () => setState(readFlag());
    window.addEventListener('storage', onStorage);
    window.addEventListener('customer-view-changed', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('customer-view-changed', onLocal);
    };
  }, []);

  const setCustomerView = useCallback((v) => {
    try {
      if (v) sessionStorage.setItem(KEY, '1');
      else sessionStorage.removeItem(KEY);
    } catch { /* ignore */ }
    window.dispatchEvent(new Event('customer-view-changed'));
  }, []);

  const toggle = useCallback(() => setCustomerView(!isCustomerView), [isCustomerView, setCustomerView]);

  return { isCustomerView, toggle, setCustomerView };
};

export default useCustomerView;
